/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useLocationHistoryStack } from "../../../../hooks/locationHistoryStack";
import debounce from 'lodash/debounce';
import { ETypeJob, IJob } from "../../../../interfaces/jobs";
import { listJobs, showJob, updateJob } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT: IJob = {
  payload: {},
  idUser: "",
  type: ETypeJob.TOWERMANAGEMENT,
  logs: [],
  id: ''
};

export const useJobs = ({
  filter: filterParam,
  includes = [],
  disabledShowId,
  disabledGetAll,
  limit: limitParam,
}: {
  disabledGetAll?: boolean;
  filter?: Record<string, any>;
  includes?: string[];
  disabledShowId?: boolean;
  limit?: number;
}) => {
  const { idJob: id } = useParams();
  const { previous } = useLocationHistoryStack();
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [job, setJob] = useState<IJob>(DEFAULT);
  const { filter, setFilter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterParam, defaultLimit: limitParam || 25 });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [enableChangePositionMap, setEnableChangePositionMap] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const pathNew = `/towermanagement/jobs/new`;
  const pathBack = `/towermanagement/jobs`;

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleJob = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setJob((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const goEdit = (id: string | number) => {
    const path = `/towermanagement/jobs/${id}`;
    navigate(path);
  };
  const goBack = () => {
    if (previous?.pathname.includes(pathNew)) {
      navigate(pathBack);
      return;
    }
    navigate(-1);
  };

  const getAll = useCallback(debounce(({
    page = 1,
    limit: limitP,
    filter
  }: {
    page?: number;
    limit?: number;
    filter?: any
  }) => {
    setLoading(true);
    listJobs({
      includes: [...includes],
      filter: filter,
      page,
      limit: limitP || limit,
    })
      .then(
        ({
          jobs: listJobs,
          limit,
          currentPage,
          totalItems,
        }) => {
          setJobs(listJobs);
          setPage(currentPage);
          setLimit(limit);
          setTotalItems(totalItems);
        }
      )
      .finally(() => setLoading(false));
  }, 300), []);

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showJob({ id, includes: [...includes, 'logs'] })
      .then(({ job: jobsShow }) => {
        setJob(jobsShow);
      })
      .finally(() => setLoading(false));
  };

  const search = useEntitySearch({ list: listJobs, key: "jobs", includes, filter });



  const update = () => {
    setLoading(true);
    updateJob({ id: job.id!, job })
      .then(() => {
        toast.success(`Medição atualizada com sucesso!`);
        navigate(0)
      })
      .finally(() => setLoading(false));
  };

  const goAdd = () => {
    navigate(pathNew);
  };

  useEffect(() => {
    setFilter((f) => ({...f,...(filterParam || {})}))
  }, [filterParam])


  useEffect(() => {
    if (!disabledGetAll)  getAll({ page, limit: limit, filter });
  }, [page, limit, filter]);

  useEffect(() => {
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  return {
    jobs,
    job,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleJob,
    id,
    update,
    enableChangePositionMap,
    setEnableChangePositionMap,
    totalItems,
    search,
    setFilter,
    handleFilter,
    get
  };
};
